import { ReactNode, useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { MoreMenu } from './moreMenu'
import * as css from './styles'

export interface ListType {
  label: string
  path: string
  selected?: boolean
  visible?: boolean
  badge?: () => ReactNode | null
  subtitle?: string
}

interface Props {
  list: ListType[]
  parentWidth: number
}

function pxStringToNum(val: string | undefined): number {
  if (!val) return 0
  return +val.split('px').join('')
}

export const NavList = ({ list, parentWidth }: Props) => {
  const listRefElem = useRef<HTMLUListElement | null>(null)
  const [showMoreNavLink, setShowMoreNavLink] = useState(false)

  useEffect(() => {
    if (!listRefElem?.current) return
    const listElemNode = listRefElem?.current
    const moreListNode = document.querySelector('#more-item-sublist .MuiList-root')
    const moreItemNode = listElemNode.querySelector('.more-item')
    const listItems = listElemNode.querySelectorAll('li:not(.more-item)')
    const listElemStyles = window.getComputedStyle(listElemNode)
    const listItemsWidth = listElemStyles.width.split('px')[0] ?? '0'
    const firstMoreItemNode = moreListNode?.firstElementChild
    const firstMoreItemStyles = firstMoreItemNode
      ? window.getComputedStyle(firstMoreItemNode)
      : null
    const firstMoreItemWidth = pxStringToNum(firstMoreItemStyles?.width)
    const moreItems = document.querySelectorAll('#more-item-sublist li')
    const firstNavMoreItemNode = moreItems[0]
    const listWidthRemaining = Math.floor(parentWidth - +listItemsWidth)

    if (!moreListNode) return

    if (moreItems?.length > 0 && !showMoreNavLink) {
      setShowMoreNavLink(true)
    } else if ((!moreItems || moreItems?.length === 0) && showMoreNavLink) {
      setShowMoreNavLink(false)
    }

    // Add fall off item to the more list.
    if (listWidthRemaining <= 0) {
      const lastItemNode = listItems[listItems.length - 1]!
      moreListNode.prepend(lastItemNode)
    }

    // Remove first item in the more list and move back to nav list.
    if (listWidthRemaining > firstMoreItemWidth && firstNavMoreItemNode) {
      moreItemNode?.before(firstNavMoreItemNode)
    }
  }, [parentWidth, listRefElem?.current])

  return (
    <css.List ref={ref => (listRefElem.current = ref)} data-testid="nav-list">
      {list?.map(({ label, path, selected = false, visible = true, badge = null }) => {
        if (!visible) return null
        return (
          <css.ListItem
            key={label}
            className={['list-item', selected ? 'is-active' : null].join(' ')}
            data-testid={`list-item-${label}`}
          >
            <Link to={path} data-testid={`nav-link-${label}`}>
              {badge ? badge() : label}
            </Link>
          </css.ListItem>
        )
      })}
      {showMoreNavLink && (
        <css.ListItem key="more" className="list-item more-item">
          <MoreMenu showMoreNavLink={showMoreNavLink} />
        </css.ListItem>
      )}
    </css.List>
  )
}
